import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import Lottie from "lottie-react";
import FloatingInput from "../../components/FloatingInput";
import TextPressure from "../../components/TextPressure";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import { Eye, EyeOff } from "lucide-react";
import dermatAnim from "./Dermatologist.json";
import footerImage from "./Footer.png"; // full width footer
// import { useLogin } from "../../hooks/useApi"; // Commented out - password login
import { useSuperadminSendOtp, useSuperadminVerifyOtp } from "../../hooks/useApi";
import { toast } from "react-toastify";
import Privacypolicy from "@/components/Privacypolicy";



const SuperadminLogin = () => {
  const navigate = useNavigate();

  const { login } = useAuth();
  const titleRef = useRef(null);
  const charRefs = useRef([]);
  const [showPage, setShowPage] = useState(false);
  const [email, setEmail] = useState("");
  // Password login - commented out
  // const [password, setPassword] = useState("");
  // const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false); // State to handle email field disable after OTP is sent
  
  // Password login hook - commented out
  // const { mutate } = useLogin();
  
  // OTP login hooks
  const { mutate: superadminSendOtpMutation, isPending: isSendingOtp } = useSuperadminSendOtp();
  const { mutate: superadminVerifyOtpMutation, isPending: isVerifyingOtp } = useSuperadminVerifyOtp();
  
  const titleText = "Hello, Dermat !";
  const chars = titleText.split("");

  // Password login handler - commented out
  // const handleLogin = () => {
  //   if (!email || !password) {
  //     toast.error("Please enter email and password");
  //     return;
  //   }

  //   const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  //   if (!emailRegex.test(email)) {
  //     toast.error("Please enter a valid email");
  //     return;
  //   }

  //   mutate(
  //     { email, password },
  //     {
  //       onSuccess: (data) => {
  //         toast.success("Login successful");
  //         login(data.data); 

  //           navigate("/superadmin-dashboard");
          
  //       },
  //       onError: (error) => {
  //         toast.error(error.response?.data?.message || "Login failed");
  //       },
  //     }
  //   );
  // };

  // OTP login handlers
  const handleEmailSendOtp = () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    superadminSendOtpMutation(
      { email: email },
      {
        onSuccess: (data) => {
          setOtpSent(true);
          toast.success('OTP sent successfully!');
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Failed to send OTP. Please try again.');
        },
      }
    );
  };

  const handleChangeEmail = () => {
    setOtpSent(false);
    setOtp("");
  };

  const handleEmailLogin = () => {
    if (!email || !otp) {
      toast.error('Please enter email and OTP');
      return;
    }

    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    superadminVerifyOtpMutation(
      { email: email, otp: otp },
      {
        onSuccess: (data) => {
          // Handle successful login - store user data and navigate
          // API response structure: {status, message, data: {user, access_token, token_type}}
          const responseData = data?.data || data;
          if (responseData?.user || responseData?.access_token) {
            login(responseData);
            toast.success('Login successful!');
          }
          navigate("/superadmin-dashboard");
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Invalid OTP. Please try again.');
        },
      }
    );
  };
 

  useEffect(() => {
    // Wait for refs to be populated
    const timer = setTimeout(() => {
      if (!titleRef.current) return;

      // Filter out null refs
      const validRefs = charRefs.current.filter(ref => ref !== null);
      if (validRefs.length === 0) return;

      // Animate each character
      validRefs.forEach((charRef, index) => {
        if (!charRef) return;
        
        gsap.fromTo(
          charRef,
          {
            opacity: 0,
            y: 40,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            delay: index * 0.04,
            ease: "power3.out",
          }
        );
      });

      // After all characters are animated, fade out the title
      const totalDuration = validRefs.length * 0.04 + 0.6;
      gsap.delayedCall(totalDuration + 0.5, () => {
        if (titleRef.current) {
          gsap.to(titleRef.current, {
            y: -200,
            opacity: 0,
            duration: 1,
            ease: "power3.inOut",
            onComplete: () => setShowPage(true),
          });
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, []);


  return (
    <>
    


      {/* upper div - White Card Design */}
      <div className="w-full h-screen relative overflow-hidden" style={{
        background: 'linear-gradient(50deg, #E5E7EB 50%, #E5E7EB 50%, #008C9E 50%, #008C9E 100%)'
      }}>
        {/* TITLE ANIMATION */}
        <h1
          ref={titleRef}
          className="absolute top-8 left-1/2 -translate-x-1/2 text-4xl font-bold text-white pointer-events-none z-10 flex items-center justify-center"
        >
          {chars.map((char, index) => (
            <span
              key={index}
              ref={(el) => {
                if (el) charRefs.current[index] = el;
              }}
              className="inline-block"
              style={{ opacity: 0, transform: 'translateY(40px)' }}
            >
              {char === " " ? "\u00A0" : char}
            </span>
          ))}
        </h1>

        {/* FULL UI SHOWN AFTER TEXT HIDES */}
        {showPage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="w-full h-full flex overflow-auto justify-center items-center px-4 py-4 sm:py-0"
          >
            {/* White Card Container */}
            <div className="w-full mt-12 sm:mt-20 max-w-4xl min-h-[60vh] sm:h-[70vh] bg-white rounded-2xl shadow-2xl  flex flex-col sm:flex-row">
              {/* LEFT PANEL – LOTTIE ANIMATION */}
              <div className="w-full lg:w-1/2 relative flex justify-center items-center py-8 sm:py-12 lg:py-0" style={{ background: "#f8f9ff" }}>
                {/* Background building illustration */}
                <img
                  src={footerImage}
                  className="absolute -bottom-8 left-0 w-full h-1/3 opacity-5 object-cover"
                  alt="background"
                />
                
                <div className="relative z-10 w-[250px] h-[250px] sm:w-[350px] sm:h-[350px] lg:w-[430px] lg:h-[430px] flex items-center justify-center">
                  <Lottie animationData={dermatAnim} loop />
                </div>
              </div>

              {/* RIGHT PANEL – LOGIN FORM */}
              <div className="w-full lg:w-1/2 relative flex flex-col justify-center items-center p-6 sm:p-8 lg:p-10 bg-white">
                {/* Background building illustration */}
                <img
                  src={footerImage}
                  className="absolute -bottom-8 right-0 w-full h-1/3 opacity-5 object-cover"
                  alt="background"
                />
                
                <div className="relative z-10 w-full max-w-md">
                  {/* <h2 className="text-3xl mb-4 font-bold mb-2 text-center text-[#008C9E]">
                    Before & After
                  </h2> */}
                   <div className="h-12 sm:h-16 mb-4 sm:mb-6 flex items-center justify-center">
                    <TextPressure
                      text="Before & After"
                      flex={true}
                      alpha={false}
                      stroke={false}
                      width={true}
                      weight={true}
                      italic={true}
                      textColor="#008C9E"
                      strokeColor="#008C9E"
                      minFontSize={18}
                      className="h-full"
                    />
                    {/* <BlurText
  text="Before & After"
  delay={150}
  animateBy="words"
  direction="top"
  
  className="text-3xl  text-[#008C9E]"
/> */}
                  </div>

                  {/* Phone login input - commented out */}
                  {/* <FloatingInput 
                    label="Mobile Number" 
                    type="tel" 
                    value={mobileNumber}
                    maxlength={10}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && mobileNumber && !otpSent) {
                        handlePhoneSendOtp();
                      } else if (e.key === 'Enter' && mobileNumber && otp && otpSent) {
                        handleLogin();
                      }
                    }}
                    disabled={otpSent}
                  /> */}

                  {/* Email login input */}
                  <FloatingInput 
                    label="Email" 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && email && !otpSent) {
                        handleEmailSendOtp();
                      } else if (e.key === 'Enter' && email && otp && otpSent) {
                        handleEmailLogin();
                      }
                    }}
                    disabled={otpSent}
                  />
                  
                  {otpSent && (
                    <>
                      <FloatingInput 
                        label="OTP" 
                        type="text" 
                        maxlength={6}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        onKeyPress={(e) => {
                          if (!/^\d*$/.test(e.key)) {
                            e.preventDefault();
                          }
                          if (e.key === 'Enter' && email && otp) {
                            handleEmailLogin();
                          }
                        }}
                      />
                      <button
                        onClick={handleChangeEmail}
                        className="text-sm text-[#008C9E] hover:underline self-start"
                      >
                        Change Email
                      </button>
                    </>
                  )}

                  {!otpSent ? (
                    <button 
                      onClick={handleEmailSendOtp}
                      disabled={isSendingOtp || !email}
                      className="w-full bg-[#008C9E] cursor-pointer text-white py-3 rounded-lg font-semibold hover:bg-[#007a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSendingOtp ? 'Sending OTP...' : 'Send OTP'}
                    </button>
                  ) : (
                    <button 
                      onClick={handleEmailLogin}
                      disabled={isVerifyingOtp || !otp || otp.length !== 6}
                      className="w-full bg-[#008C9E] cursor-pointer text-white py-3 rounded-lg font-semibold mt-4 hover:bg-[#007a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isVerifyingOtp ? 'Verifying...' : 'Login'}
                    </button>
                  )}

                  {/* Password login - commented out */}
                  {/* <FloatingInput 
                    label="Email" 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && email && password) {
                        handleLogin();
                      }
                    }}
                    disabled={otpSent}
                  />
                  <div className="relative w-full mb-5">
                    <FloatingInput 
                      label="Password" 
                      type={showPassword ? "text" : "password"} 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && email && password) {
                          handleLogin();
                        }
                      }}
                      hasRightIcon={true}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer z-10"
                      style={{ marginTop: '6px' }}
                    >
                      {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                  </div>

                  <button 
                    onClick={handleLogin}
                    className="w-full bg-[#008C9E] cursor-pointer text-white py-3 rounded-lg font-semibold mt-4 hover:bg-[#007a8a] transition-colors"
                  >
                    Login
                  </button> */}

                  <div className="flex flex-col items-center  gap-3">
                  <p className="text-center  text-sm text-gray-500 mt-6">
                    Developed by <a href="https://kanishkasoftware.com" target="_blank" className="cursor-pointer font-bold text-[#008C9E]">Kanishka Software</a>
                    
                  </p>
                  <p onClick={() => navigate("/privacy-policy")} className="cursor-pointer text-gray-500 text-sm" >Privacy Policy</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default SuperadminLogin;
